package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.DataExport
import com.evoapps.evoshift.domain.model.ExportLimitReachedException
import com.evoapps.evoshift.domain.model.RecentSignInRequiredException
import com.evoapps.evoshift.domain.repository.DataExportRepository
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonObjectBuilder
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put

/** The exportMyData / exportWorkplace callables (functions/dataExport.js). */
class DataExportRepositoryImpl(private val functions: CloudFunctionsClient) : DataExportRepository {

    override suspend fun exportMyData(language: String): Result<DataExport> = export("exportMyData") {
        put("language", language)
    }

    override suspend fun exportWorkplace(workplaceId: String, employeeUid: String?, language: String): Result<DataExport> =
        export("exportWorkplace") {
            put("workplaceId", workplaceId)
            if (employeeUid != null) put("employeeUid", employeeUid)
            put("language", language)
        }

    private suspend fun export(
        name: String,
        data: JsonObjectBuilder.() -> Unit
    ): Result<DataExport> = try {
        Result.success(parse(functions.call(name, buildJsonObject(data))))
    } catch (e: CloudFunctionsException) {
        Result.failure(
            when (e.code) {
                // The only precondition of the exports: a sign-in within the last 10 minutes.
                "failed-precondition" -> RecentSignInRequiredException()
                "resource-exhausted" -> ExportLimitReachedException()
                else -> e
            }
        )
    } catch (e: Exception) {
        Result.failure(e)
    }

    companion object {
        /** The callable's answer: {url, expiresAt, fileName, sizeBytes, files, counts}. */
        fun parse(result: JsonElement): DataExport {
            val o: JsonObject = result.jsonObject
            fun text(key: String) = o[key]?.jsonPrimitive?.contentOrNull.orEmpty()
            return DataExport(
                url = text("url").ifEmpty { error("The export has no download link") },
                expiresAt = text("expiresAt"),
                fileName = text("fileName"),
                sizeBytes = o["sizeBytes"]?.jsonPrimitive?.longOrNull ?: 0L,
                files = o["files"]?.jsonArray?.mapNotNull { it.jsonPrimitive.contentOrNull }.orEmpty(),
                counts = o["counts"]?.jsonObject?.mapNotNull { (k, v) -> v.jsonPrimitive.intOrNull?.let { k to it } }
                    ?.toMap().orEmpty()
            )
        }
    }
}
